import apiClient from "./apiClient";

// expira en 10 minutos
const CACHE_DURATION = 10 * 60 * 1000; 

const isCacheValid = (timestamp) => {
  if (!timestamp) return false;
  const now = Date.now();
  const lastUpdate = parseInt(timestamp);
  return (now - lastUpdate) < CACHE_DURATION;
};

const invalidateCache = () => {
  localStorage.removeItem('userOrders');
  localStorage.removeItem('userOrdersTimestamp');
  localStorage.removeItem('adminOrders');
  localStorage.removeItem('ordersTimestamp');
};

export const prependUserOrderCache = (newOrder) => {
  if (!newOrder || !newOrder.id) return;

  try {
    const cachedData = localStorage.getItem('userOrders');
    const existing = cachedData ? JSON.parse(cachedData) : [];
    const currentList = Array.isArray(existing) ? existing : [];

    const withoutDuplicate = currentList.filter((order) => order.id !== newOrder.id);
    const nextOrders = [newOrder, ...withoutDuplicate];

    localStorage.setItem('userOrders', JSON.stringify(nextOrders));
    localStorage.setItem('userOrdersTimestamp', Date.now().toString());
  } catch (error) {
    console.error('Error updating user orders cache:', error);
  }
};

export const getUserOrders = async () => {
  try {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    // Intentar obtener órdenes del caché primero
    const cachedData = localStorage.getItem('userOrders');
    const cacheTimestamp = localStorage.getItem('userOrdersTimestamp');
    
    if (cachedData && cacheTimestamp && isCacheValid(cacheTimestamp)) {
      return JSON.parse(cachedData);
    }

    const response = await apiClient.get('/orders/my-orders/', {
      headers: {
        Authorization: `Bearer ${token}`
      },
      params: {
        include_items: true
      }
    });
    
    // Si la respuesta está vacía, devolver array vacío
    if (!response.data) return [];
    
    // Manejar tanto respuestas paginadas como no paginadas
    const orders = response.data.results || response.data;
    
    // Guardar en caché
    localStorage.setItem('userOrders', JSON.stringify(orders));
    localStorage.setItem('userOrdersTimestamp', Date.now().toString());
    
    return orders;
  } catch (error) {
    if (error.response?.status === 401) {
      // Si hay error de autenticación, intentar limpiar los datos almacenados
      localStorage.removeItem('userOrders');
      localStorage.removeItem('userOrdersTimestamp');
      
      throw new Error('Sesión expirada o inválida');
    }
    if (error.response?.status === 404) {
      return []; // Devolver array vacío si no hay órdenes
    }
    console.error('Error fetching user orders:', error);
    throw new Error(error.response?.data?.error || 'Error al obtener los pedidos');
  }
};

export const getOrders = async (ignoreCache = false, includeTemporaryCarts = false, startDate = null, endDate = null) => {
  try {
    // Si tenemos fechas específicas, no usar caché
    const useCache = !ignoreCache && !startDate && !endDate;
    
    const cachedData = localStorage.getItem('adminOrders');
    const cacheTimestamp = localStorage.getItem('ordersTimestamp');
    
    if (useCache && cachedData && cacheTimestamp && isCacheValid(cacheTimestamp)) {
      return JSON.parse(cachedData);
    }

    const params = {
      include_items: true,
      exclude_temporary: !includeTemporaryCarts // Solo incluir órdenes completas a menos que se especifique lo contrario
    };
    
    // Añadir filtros de fecha si se especifican
    if (startDate) {
      params.start_date = startDate;
    }
    if (endDate) {
      params.end_date = endDate;
    }

    const response = await apiClient.get("/orders/", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
      },
      params: params
    });
    
    // Asegurarse de que siempre devolvemos un array
    const orders = Array.isArray(response.data) ? response.data : response.data.results || [];
    
    localStorage.setItem('adminOrders', JSON.stringify(orders));
    localStorage.setItem('ordersTimestamp', Date.now().toString());
    
    return orders;
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw new Error(error.response?.data?.message || 'Error al obtener las órdenes');
  }
};

export const createOrder = async (orderData) => {
  try {
    // Añadir el anonymous_id si existe un carrito temporal
    const anonymousId = localStorage.getItem('cart_anonymous_id');
    if (anonymousId) {
      orderData.anonymous_id = anonymousId;
    }
    
    const response = await apiClient.post('/orders/', orderData, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        'Content-Type': 'application/json'
      }
    });
    
    // Si la orden se creó exitosamente, limpiar el ID del carrito anónimo
    if (response.data && response.data.id) {
      localStorage.removeItem('cart_anonymous_id');
    }
    
    return response.data;
  } catch (error) {
    if (error.response?.data) {
      // Handle validation errors
      const validationErrors = {};
      Object.keys(error.response.data).forEach(key => {
        validationErrors[key] = Array.isArray(error.response.data[key]) 
          ? error.response.data[key][0] 
          : error.response.data[key];
      });
      throw { validationErrors };
    }
    throw new Error('Error al crear la orden');
  }
}

/**
 * Validate cart items against backend PriceService before checkout.
 * Returns current prices and flags if any price has changed.
 */
export const validateCart = async (cartItems) => {
  const items = cartItems.map(item => ({
    product_id: item.id,
    quantity: item.quantity,
    variants: item.variants || {},
    price: item.price,
  }));
  const response = await apiClient.post('/orders/validate-cart/', { items });
  return response.data;
};

export const getOrderDetails = async (orderId) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.get(`/orders/${orderId}/`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      throw new Error('La orden solicitada no existe');
    }
    throw new Error(error.response?.data?.message || 'Error al obtener los detalles de la orden');
  }
};

export const getOrder = async (id) => {
  apiClient.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem('accessToken')}`;
  const response = await apiClient.get(`/orders/${id}/`);
  return response.data;
};

/**
 * Obtiene el campo `status` de varios pedidos (GET /orders/:id/) en lotes concurrentes.
 * Las claves del mapa son strings del id para coincidir con order_id del envío.
 * @param {Array<number|string>} orderIds
 * @param {{ concurrency?: number }} [options]
 * @returns {Promise<Record<string, string|null>>}
 */
export const fetchOrderStatusMapByIds = async (orderIds, options = {}) => {
  const concurrency = Math.max(1, Math.min(24, options.concurrency ?? 12));
  const token = localStorage.getItem('accessToken');
  const unique = [
    ...new Set(
      (orderIds || [])
        .map((id) => (id == null ? null : String(id)))
        .filter((id) => id != null && id !== ''),
    ),
  ];
  const map = {};

  for (let i = 0; i < unique.length; i += concurrency) {
    const slice = unique.slice(i, i + concurrency);
    const results = await Promise.all(
      slice.map(async (id) => {
        try {
          const response = await apiClient.get(`/orders/${id}/`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          });
          return [id, response.data?.status ?? null];
        } catch {
          return [id, null];
        }
      }),
    );
    for (const [id, status] of results) {
      map[id] = status;
    }
  }

  return map;
};

export const updateOrder = async (orderId, orderData) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.put(`/orders/${orderId}/`, orderData, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    // Invalidar todos los cachés al actualizar una orden
    invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error en updateOrder:', error.response?.data);
    throw new Error(error.response?.data?.message || 'Error al actualizar el pedido');
  }
};

export const updateOrderStatus = async (orderId, status) => {
  try {
    const token = localStorage.getItem('accessToken');
    const response = await apiClient.patch(`/orders/${orderId}/update_status/`, { status }, {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });
    // Invalidar todos los cachés al actualizar una orden
    invalidateCache();
    return response.data;
  } catch (error) {
    console.error('Error en updateOrderStatus:', error.response?.data);
    throw new Error(error.response?.data?.message || error.response?.data?.error || 'Error al actualizar el estado del pedido');
  }
};

export const deleteOrder = async (id) => {
  apiClient.defaults.headers.common["Authorization"] = `Bearer ${localStorage.getItem('accessToken')}`;
  const response = await apiClient.delete(`/orders/${id}/`);
  return response.data;
};

export const getCustomerOrders = async () => {
  try {
    const response = await apiClient.get('/orders/my-orders/', 
      {headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`
      },}
    );
    return response.data;
  } catch (error) {
    console.error('Error fetching customer orders:', error);
    throw error;
  }
}

export const cancelOrder = async (orderId) => {
  const response = await apiClient.post(`/orders/${orderId}/cancel/`);
  return response.data;
};

export const contactOrder = async (orderId, message) => {
  try {
    const response = await apiClient.post(`/orders/${orderId}/contact/`, {
      message
    }, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        'Content-Type': 'application/json'
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(
      error.response?.data?.message ||
      error.response?.data?.error ||
      'Error al enviar el mensaje'
    );
  }
};

export const updateOrderItems = async (orderId, items) => {
  try {
    const response = await apiClient.patch(`/orders/${orderId}/items/`, {
      items: items
    }, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`,
        'Content-Type': 'application/json'
      }
    })
    invalidateCache()
    return response.data
  } catch (error) {
    console.error('Error updating order items:', error.response || error)
    throw new Error(error.response?.data?.error || error.response?.data?.message || 'Error al actualizar los productos')
  }
}

export const notifyShipping = async (orderId) => {
  try {
    const response = await apiClient.post(`/orders/${orderId}/notify_shipping/`, null, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || 'Error al enviar la notificación de envío');
  }
};


// Las funciones para manejar carritos abandonados se han movido a services/abandonedCarts.js

/**
 * Ejecuta el comando del sistema para actualizar el tipo de cambio y los carritos abandonados
 * @param {Object} options - Opciones para el comando
 * @param {boolean} options.skipDollar - Si se debe omitir la actualización del tipo de cambio
 * @param {boolean} options.skipCarts - Si se debe omitir la actualización de carritos abandonados
 * @param {number} options.minutes - Umbral de tiempo en minutos para considerar un carrito abandonado
 * @param {boolean} options.dryRun - Si se debe ejecutar en modo simulación sin realizar cambios
 * @returns {Promise<Obwject>} Resultado de la operación
 */
export const runSystemUpdate = async (options = {}) => {
  try {
    const response = await apiClient.post('/orders/run_system_update/', options, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('accessToken')}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error al ejecutar actualización del sistema:', error);
    throw new Error(error.response?.data?.message || 'Error al ejecutar actualización del sistema');
  }
};
